import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const TalentForm = ({ initialData, onSave, onCancel, saving, isEditing }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    display_name: '',
    email: '',
    phone: '',
    instagram: '',
    youtube: '',
    tiktok: '',
    website: '',
    headshot_url: '',
    bio: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [headshotFile, setHeadshotFile] = useState(null);
  const [headshotPreview, setHeadshotPreview] = useState(null);
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (initialData) {
      console.log('TalentForm initializing with data:', initialData);
      const formDataFromInitial = {
        first_name: initialData?.first_name || '',
        last_name: initialData?.last_name || '',
        display_name: initialData?.display_name || '',
        email: initialData?.email || '',
        phone: initialData?.phone || '',
        instagram: initialData?.instagram || '',
        youtube: initialData?.youtube || '',
        tiktok: initialData?.tiktok || '',
        website: initialData?.website || '',
        headshot_url: initialData?.headshot_url || '',
        bio: initialData?.bio || ''
      };
      console.log('Setting form data:', formDataFromInitial);
      setFormData(formDataFromInitial);
      
      // Load existing headshot preview
      if (initialData?.headshot_url) {
        loadHeadshotPreview(initialData?.headshot_url);
      }
    } else {
      // Reset form when no initial data
      setFormData({
        first_name: '',
        last_name: '',
        display_name: '',
        email: '',
        phone: '',
        instagram: '',
        youtube: '',
        tiktok: '',
        website: '',
        headshot_url: '',
        bio: ''
      });
      setHeadshotPreview(null);
      setHeadshotFile(null);
    }
  }, [initialData]);

  // Load headshot preview from storage
  const loadHeadshotPreview = async (headshotPath) => {
    if (!headshotPath) return;
    
    try {
      const { data, error } = await supabase?.storage?.from('profile-images')?.createSignedUrl(headshotPath, 3600); // 1 hour expiry
      
      if (error) throw error;
      setHeadshotPreview(data?.signedUrl);
    } catch (error) {
      console.error('Error loading headshot preview:', error);
    }
  };

  // Clear errors when initialData changes
  useEffect(() => {
    if (initialData) {
      setErrors({});
      setTouched({});
    }
  }, [initialData]);

  // Validation
  const validateField = (name, value) => {
    switch (name) {
      case 'first_name':
        if (!value?.trim()) return 'First name is required';
        if (value?.trim()?.length < 2) return 'First name must be at least 2 characters';
        return null;
      case 'last_name':
        if (!value?.trim()) return 'Last name is required';
        if (value?.trim()?.length < 2) return 'Last name must be at least 2 characters';
        return null;
      case 'email':
        if (!value?.trim()) return null; // Optional field
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex?.test(value) ? null : 'Please enter a valid email address';
      case 'phone':
        if (!value?.trim()) return null; // Optional field
        const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
        return phoneRegex?.test(value) ? null : 'Please enter a valid phone number';
      case 'website':
        if (!value?.trim()) return null; // Optional field
        try {
          new URL(value?.startsWith('http') ? value : `https://${value}`);
          return null;
        } catch {
          return 'Please enter a valid website URL (e.g., https://example.com)';
        }
      case 'instagram':
        if (!value?.trim()) return null; // Optional field
        // Accept full URL or username
        if (value?.includes('instagram.com/')) {
          try {
            new URL(value?.startsWith('http') ? value : `https://${value}`);
            return null;
          } catch {
            return 'Please enter a valid Instagram URL';
          }
        } else if (value?.startsWith('@')) {
          return value?.length > 1 ? null : 'Please enter a valid Instagram username';
        } else {
          return /^[a-zA-Z0-9._]+$/?.test(value) ? null : 'Please enter a valid Instagram username';
        }
      case 'youtube':
        if (!value?.trim()) return null; // Optional field
        if (value?.includes('youtube.com/') || value?.includes('youtu.be/')) {
          try {
            new URL(value?.startsWith('http') ? value : `https://${value}`);
            return null;
          } catch {
            return 'Please enter a valid YouTube URL';
          }
        }
        return 'Please enter a valid YouTube URL';
      case 'tiktok':
        if (!value?.trim()) return null; // Optional field
        // Accept full URL or username
        if (value?.includes('tiktok.com/')) {
          try {
            new URL(value?.startsWith('http') ? value : `https://${value}`);
            return null;
          } catch {
            return 'Please enter a valid TikTok URL';
          }
        } else if (value?.startsWith('@')) {
          return value?.length > 1 ? null : 'Please enter a valid TikTok username';
        } else {
          return /^[a-zA-Z0-9._]+$/?.test(value) ? null : 'Please enter a valid TikTok username';
        }
      default:
        return null;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation - validate as user types
    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e?.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate all fields
    Object.keys(formData)?.forEach(fieldName => {
      const error = validateField(fieldName, formData?.[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    setTouched(Object.keys(formData)?.reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    
    return isValid;
  };

  // Handle headshot file selection
  const handleHeadshotChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes?.includes(file?.type)) {
      setErrors(prev => ({
        ...prev,
        headshot: 'Please select a valid image file (JPEG, PNG, or WebP)'
      }));
      return;
    }

    // Validate file size (5MB max)
    if (file?.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        headshot: 'Image size must be less than 5MB'
      }));
      return;
    }

    setHeadshotFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setHeadshotPreview(reader?.result);
    };
    reader?.readAsDataURL(file);

    // Clear any existing errors
    if (errors?.headshot) {
      setErrors(prev => ({
        ...prev,
        headshot: null
      }));
    }
  };

  // Remove headshot
  const handleRemoveHeadshot = () => {
    setHeadshotFile(null);
    setHeadshotPreview(null);
    setFormData(prev => ({
      ...prev,
      headshot_url: ''
    }));
  };

  // Upload headshot to storage
  const uploadHeadshot = async (talentId) => {
    if (!headshotFile) return formData?.headshot_url; // Return existing URL if no new file

    setUploadingHeadshot(true);
    try {
      // Delete old headshot if exists
      if (formData?.headshot_url) {
        try {
          await supabase?.storage?.from('profile-images')?.remove([formData?.headshot_url]);
        } catch (deleteError) {
          console.error('Error deleting old headshot:', deleteError);
        }
      }

      // Upload new headshot
      const fileExt = headshotFile?.name?.split('.')?.pop();
      const fileName = `${talentId}-${Date.now()}.${fileExt}`;
      const filePath = `talent-headshots/${fileName}`;

      const { error: uploadError } = await supabase?.storage?.from('profile-images')?.upload(filePath, headshotFile);

      if (uploadError) throw uploadError;

      return filePath;
    } catch (error) {
      console.error('Error uploading headshot:', error);
      throw error;
    } finally {
      setUploadingHeadshot(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Clean up data before sending
      const cleanData = Object.entries(formData)?.reduce((acc, [key, value]) => {
        if (key !== 'headshot_url') {
          acc[key] = value?.trim() || null;
        }
        return acc;
      }, {});

      // If there's a new headshot file, we'll handle it in the parent component
      // Pass the file along with the form data
      const dataToSave = {
        ...cleanData,
        headshotFile: headshotFile,
        headshot_url: formData?.headshot_url
      };

      const success = await onSave?.(dataToSave);
      if (!success) {
        // Keep form open on error
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors(prev => ({
        ...prev,
        submit: error?.message || 'Failed to save talent profile'
      }));
    }
  };

  const hasError = (fieldName) => touched?.[fieldName] && errors?.[fieldName];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Headshot Upload Section */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Headshot Photo</h3>
        
        <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
          {/* Preview */}
          <div className="flex-shrink-0">
            {headshotPreview ? (
              <div className="relative">
                <img
                  src={headshotPreview}
                  alt="Headshot preview"
                  className="w-32 h-32 rounded-full object-cover border-4 border-white border-opacity-30"
                />
                <button
                  type="button"
                  onClick={handleRemoveHeadshot}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                  title="Remove headshot"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            ) : (
              <div className="w-32 h-32 rounded-full bg-white bg-opacity-10 border-2 border-dashed border-white border-opacity-30 flex items-center justify-center">
                <Icon name="User" size={40} className="text-white text-opacity-30" />
              </div>
            )}
          </div>

          {/* Upload Controls */}
          <div className="flex-1">
            <label
              htmlFor="headshot"
              className="inline-flex items-center space-x-2 px-4 py-2 bg-white text-indigo-900 font-medium rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
            >
              <Icon name="Upload" size={18} />
              <span>{headshotPreview ? 'Change Headshot' : 'Upload Headshot'}</span>
            </label>
            <input
              id="headshot"
              name="headshot"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleHeadshotChange}
              className="hidden"
            />
            
            <p className="text-xs text-blue-100 mt-2">
              Upload a professional headshot photo (JPEG, PNG, or WebP, max 5MB)
            </p>
            
            {errors?.headshot && (
              <p className="text-sm text-red-300 mt-2">{errors?.headshot}</p>
            )}
            
            {uploadingHeadshot && (
              <div className="flex items-center space-x-2 mt-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-blue-100">Uploading headshot...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Required Fields */}
        <div className="space-y-2">
          <label htmlFor="first_name" className="block text-sm font-medium text-white">
            First Name *
          </label>
          <Input
            id="first_name"
            name="first_name"
            type="text"
            value={formData?.first_name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`bg-white bg-opacity-20 border-white border-opacity-30 text-slate-900 placeholder-blue-200 focus:bg-opacity-30 focus:border-blue-300 ${hasError('first_name') ? 'border-red-400' : ''}`}
            placeholder="Enter first name"
            required
          />
          {hasError('first_name') && (
            <p className="text-sm text-red-300">{errors?.first_name}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="last_name" className="block text-sm font-medium text-white">
            Last Name *
          </label>
          <Input
            id="last_name"
            name="last_name"
            type="text"
            value={formData?.last_name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`bg-white bg-opacity-20 border-white border-opacity-30 text-slate-900 placeholder-blue-200 focus:bg-opacity-30 focus:border-blue-300 ${hasError('last_name') ? 'border-red-400' : ''}`}
            placeholder="Enter last name"
            required
          />
          {hasError('last_name') && (
            <p className="text-sm text-red-300">{errors?.last_name}</p>
          )}
        </div>

        {/* Display Name Field - spans full width */}
        <div className="md:col-span-2 space-y-2">
          <label htmlFor="display_name" className="block text-sm font-medium text-white">
            Display Name
            <span className="text-blue-200 text-xs ml-2">(Optional - if empty, will use First + Last Name)</span>
          </label>
          <Input
            id="display_name"
            name="display_name"
            type="text"
            value={formData?.display_name}
            onChange={handleChange}
            onBlur={handleBlur}
            className="bg-white bg-opacity-20 border-white border-opacity-30 text-slate-900 placeholder-blue-200 focus:bg-opacity-30 focus:border-blue-300"
            placeholder="Enter display name for public visibility"
          />
          <p className="text-xs text-blue-100">
            This name will be displayed publicly. If left empty, your first and last name will be shown instead.
          </p>
        </div>

        {/* Bio Field - spans full width */}
        <div className="md:col-span-2 space-y-2">
          <label htmlFor="bio" className="block text-sm font-medium text-white">
            Professional Bio
            <span className="text-blue-200 text-xs ml-2">(Optional)</span>
          </label>
          <textarea
            id="bio"
            name="bio"
            rows={6}
            value={formData?.bio}
            onChange={handleChange}
            onBlur={handleBlur}
            className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 text-slate-900 placeholder-blue-200 rounded-xl focus:bg-opacity-30 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors resize-vertical"
            placeholder="Enter a professional biography describing your expertise, teaching style, roles you specialize in, and what makes you unique as a musical theatre educator..."
          />
          <p className="text-xs text-blue-100">
            This biography will be displayed on your public talent profile. Describe your background, specializations, and teaching approach.
          </p>
        </div>

        {/* Optional Fields */}
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-white">
            Email
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData?.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`bg-white bg-opacity-20 border-white border-opacity-30 text-slate-900 placeholder-blue-200 focus:bg-opacity-30 focus:border-blue-300 ${hasError('email') ? 'border-red-400' : ''}`}
            placeholder="Enter email address"
          />
          {hasError('email') && (
            <p className="text-sm text-red-300">{errors?.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="phone" className="block text-sm font-medium text-white">
            Phone
          </label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData?.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`bg-white bg-opacity-20 border-white border-opacity-30 text-slate-900 placeholder-blue-200 focus:bg-opacity-30 focus:border-blue-300 ${hasError('phone') ? 'border-red-400' : ''}`}
            placeholder="Enter phone number"
          />
          {hasError('phone') && (
            <p className="text-sm text-red-300">{errors?.phone}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="instagram" className="block text-sm font-medium text-white">
            Instagram
          </label>
          <Input
            id="instagram"
            name="instagram"
            type="text"
            value={formData?.instagram}
            onChange={handleChange}
            onBlur={handleBlur}
            className="bg-white bg-opacity-20 border-white border-opacity-30 text-slate-900 placeholder-blue-200 focus:bg-opacity-30 focus:border-blue-300"
            placeholder="@username or profile URL"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="youtube" className="block text-sm font-medium text-white">
            YouTube
          </label>
          <Input
            id="youtube"
            name="youtube"
            type="text"
            value={formData?.youtube}
            onChange={handleChange}
            onBlur={handleBlur}
            className="bg-white bg-opacity-20 border-white border-opacity-30 text-slate-900 placeholder-blue-200 focus:bg-opacity-30 focus:border-blue-300"
            placeholder="Channel name or URL"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="tiktok" className="block text-sm font-medium text-white">
            TikTok
          </label>
          <Input
            id="tiktok"
            name="tiktok"
            type="text"
            value={formData?.tiktok}
            onChange={handleChange}
            onBlur={handleBlur}
            className="bg-white bg-opacity-20 border-white border-opacity-30 text-slate-900 placeholder-blue-200 focus:bg-opacity-30 focus:border-blue-300"
            placeholder="@username or profile URL"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="website" className="block text-sm font-medium text-white">
            Website
          </label>
          <Input
            id="website"
            name="website"
            type="url"
            value={formData?.website}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`bg-white bg-opacity-20 border-white border-opacity-30 text-slate-900 placeholder-blue-200 focus:bg-opacity-30 focus:border-blue-300 ${hasError('website') ? 'border-red-400' : ''}`}
            placeholder="https://example.com"
          />
          {hasError('website') && (
            <p className="text-sm text-red-300">{errors?.website}</p>
          )}
        </div>
      </div>
      
      {/* Required Fields Note */}
      <div className="bg-yellow-500 bg-opacity-20 border border-yellow-400 border-opacity-30 rounded-xl p-4">
        <p className="text-sm text-yellow-100">
          <span className="text-red-300">*</span> Required fields. 
          Only First Name and Last Name are mandatory - all other fields are optional.
        </p>
      </div>
      
      {/* Submit Error */}
      {errors?.submit && (
        <div className="bg-red-500 bg-opacity-20 border border-red-400 border-opacity-30 rounded-xl p-4">
          <p className="text-sm text-red-300">{errors?.submit}</p>
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 text-blue-200 hover:text-white font-medium transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
          
          <button
            type="submit"
            disabled={saving || uploadingHeadshot}
            className={`
              px-8 py-3 bg-white text-indigo-900 font-semibold rounded-xl
              transition-all duration-200 flex items-center space-x-2
              ${(saving || uploadingHeadshot)
                ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 hover:scale-102 shadow-lg'
              }
            `}
          >
            {(saving || uploadingHeadshot) ? (
              <>
                <div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-900 rounded-full animate-spin"></div>
                <span>{uploadingHeadshot ? 'Uploading...' : (isEditing ? 'Updating...' : 'Creating...')}</span>
              </>
            ) : (
              <>
                <Icon name={isEditing ? 'Save' : 'Plus'} size={18} />
                <span>{isEditing ? 'Update Talent' : 'Create Talent'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default TalentForm;